// Synth presets for the three period instruments (ported from the desktop app's
// `lib/synthPresets.ts`; names are brand-free). Each period picks one bass, one harmony and one
// lead preset; contributors can't change them. Presets come in families of a similar character:
// each instrument also offers two alternates from its main preset's family, which a loop may pick
// instead (variety within a day that still sounds of a piece).

import type { Envelope } from "./generated/Envelope";
import type { PadMode } from "./generated/PadMode";
import type { SynthModel } from "./generated/SynthModel";
import type { SynthParams } from "./generated/SynthParams";
import { MORE_PRESETS } from "./presetsMore";

/** The instrument slots, in slot order (16 bass … 19 classic); the atmos kit's pads follow (20–31). */
export type InstrumentRole = "bass" | "harmony" | "lead" | "classic";
export const INSTRUMENT_ROLES: readonly InstrumentRole[] = ["bass", "harmony", "lead", "classic"];

export interface SynthPreset {
  id: string;
  name: string;
  role: InstrumentRole;
  params: SynthParams;
  env: Envelope;
  mode: PadMode;
  reverb: number;
  delay: number;
  /** A short descriptive word for the calendar ("warm", "squelchy"…). */
  flavour: string;
  /** Presets of one family share a character and stand in for each other within a day. */
  family: string;
  /** Only ever an alternate: never a day's main instrument. */
  alternateOnly?: boolean;
  /** A classic (orchestral / acoustic / guitar) instrument: every day has at least one. */
  classic?: boolean;
  /** Squeaky, wobbly or very bright: example songs use it sparingly and filtered. */
  intense?: boolean;
}

const p = (model: SynthModel, note: number, o: Partial<SynthParams>): SynthParams => ({
  model,
  note,
  cutoff: 0.45,
  resonance: 0.3,
  env_amount: 0.5,
  env_decay: 300,
  glide_ms: 0,
  detune: 0.2,
  tone: 0.3,
  drive: 0.2,
  ...o,
});
const env = (attack_ms: number, decay_ms: number, sustain: number, release_ms: number): Envelope => ({
  attack_ms,
  decay_ms,
  sustain,
  release_ms,
});

const CORE_PRESETS: readonly SynthPreset[] = [
  // Bass
  {
    id: "ladder-bass",
    family: "analog-bass",
    name: "Ladder Bass",
    role: "bass",
    flavour: "round",
    params: p("mono", 36, {
      cutoff: 0.3,
      resonance: 0.35,
      env_amount: 0.6,
      env_decay: 240,
      tone: 0.2,
      drive: 0.35,
      detune: 0.25,
      glide_ms: 35,
      level: 2.082,
    }),
    env: env(2, 400, 0.7, 90),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "wood-bass",
    family: "analog-bass",
    name: "Wood Bass",
    role: "bass",
    flavour: "woody",
    params: p("mono", 36, {
      cutoff: 0.32,
      resonance: 0.35,
      env_amount: 0.6,
      env_decay: 250,
      tone: 0.25,
      drive: 0.3,
      glide_ms: 40,
      level: 2.494,
    }),
    env: env(2, 400, 0.7, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "boom-bass",
    family: "sub-bass",
    name: "Boom Bass",
    role: "bass",
    flavour: "booming",
    params: p("bass808", 36, { env_amount: 0.18, env_decay: 380, tone: 0.3, drive: 0.35, glide_ms: 60, level: 0.644 }),
    env: env(1, 100, 1, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "grit-sub",
    family: "sub-bass",
    name: "Grit Sub",
    role: "bass",
    flavour: "gritty",
    params: p("bass808", 33, { env_amount: 0.22, env_decay: 600, tone: 0.4, drive: 0.7, glide_ms: 70, level: 0.554 }),
    env: env(1, 100, 1, 150),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "acid-line",
    intense: true,
    family: "acid-bass",
    name: "Acid Line",
    role: "bass",
    flavour: "squelchy",
    params: p("acid", 36, {
      cutoff: 0.25,
      resonance: 0.85,
      env_amount: 0.7,
      env_decay: 180,
      tone: 0,
      drive: 0.4,
      glide_ms: 60,
      level: 1.042,
    }),
    env: env(1, 300, 0.6, 60),
    mode: "gate",
    reverb: 0,
    delay: 0.15,
  },
  // Harmony
  {
    id: "chorus-pad",
    family: "pad",
    name: "Chorus Pad",
    role: "harmony",
    flavour: "lush",
    params: p("poly", 60, {
      cutoff: 0.5,
      resonance: 0.15,
      env_amount: 0.25,
      env_decay: 1200,
      tone: 0.6,
      detune: 0.9,
      level: 1.072,
    }),
    env: env(400, 1500, 0.8, 1200),
    mode: "gate",
    reverb: 0.6,
    delay: 0.1,
  },
  {
    id: "poly-stab",
    family: "stab",
    name: "Poly Stab",
    role: "harmony",
    flavour: "punchy",
    params: p("poly", 60, {
      cutoff: 0.55,
      resonance: 0.3,
      env_amount: 0.55,
      env_decay: 220,
      tone: 0.3,
      detune: 0.7,
      level: 2.243,
    }),
    env: env(1, 350, 0, 200),
    mode: "one_shot",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "fm-epiano",
    family: "keys",
    name: "FM E-Piano",
    role: "harmony",
    flavour: "mellow",
    params: p("fm", 60, {
      cutoff: 0.15,
      resonance: 0.1,
      env_amount: 0.35,
      env_decay: 900,
      tone: 0.17,
      detune: 0.4,
      level: 0.812,
    }),
    env: env(1, 1800, 0.25, 400),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "jazz-organ",
    family: "organ",
    name: "Jazz Organ",
    role: "harmony",
    flavour: "smoky",
    params: p("organ", 60, { cutoff: 0.1, resonance: 0.5, detune: 0.5, tone: 0, drive: 0.15, level: 0.808 }),
    env: env(3, 100, 1, 60),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "full-drawbars",
    intense: true,
    family: "organ",
    name: "Full Drawbars",
    role: "harmony",
    flavour: "bright",
    params: p("organ", 60, { cutoff: 0.4, resonance: 0.1, detune: 0.6, tone: 0.45, drive: 0.15, level: 0.64 }),
    env: env(3, 100, 1, 80),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  // Lead
  {
    id: "ladder-lead",
    family: "ladder-lead",
    name: "Ladder Lead",
    role: "lead",
    flavour: "singing",
    params: p("mono", 60, {
      cutoff: 0.55,
      resonance: 0.45,
      env_amount: 0.45,
      env_decay: 600,
      tone: 0.6,
      detune: 0.5,
      glide_ms: 80,
      level: 1.396,
    }),
    env: env(5, 500, 0.8, 250),
    mode: "gate",
    reverb: 0.25,
    delay: 0.25,
  },
  {
    id: "ladder-brass",
    family: "ladder-lead",
    name: "Ladder Brass",
    role: "lead",
    flavour: "brassy",
    params: p("mono", 48, {
      cutoff: 0.35,
      resonance: 0.2,
      env_amount: 0.75,
      env_decay: 350,
      tone: 0.1,
      detune: 0.6,
      drive: 0.3,
      level: 1.284,
    }),
    env: env(30, 500, 0.8, 250),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "deep-organ",
    family: "deep-lead",
    name: "Deep Organ",
    role: "lead",
    flavour: "deep",
    // A short, percussive organ stab played as a riff (classic 90s deep-house organ).
    params: p("organ", 60, { cutoff: 0.32, resonance: 0.2, detune: 0.35, tone: 0.45, drive: 0.15, level: 1.583 }),
    env: env(1, 320, 0, 160),
    mode: "one_shot",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "square-squelch",
    intense: true,
    family: "acid-lead",
    name: "Square Squelch",
    role: "lead",
    flavour: "squelchy",
    params: p("acid", 48, {
      cutoff: 0.3,
      resonance: 0.75,
      env_amount: 0.8,
      env_decay: 260,
      tone: 1,
      drive: 0.6,
      level: 0.639,
    }),
    env: env(1, 250, 0.5, 60),
    mode: "gate",
    reverb: 0,
    delay: 0.2,
  },
  // Alternates (never a day's main instrument): more members for every family.
  {
    id: "pluck-bass",
    name: "Pluck Bass",
    role: "bass",
    family: "analog-bass",
    alternateOnly: true,
    flavour: "plucky",
    params: p("mono", 36, {
      cutoff: 0.28,
      resonance: 0.3,
      env_amount: 0.75,
      env_decay: 160,
      tone: 0.25,
      drive: 0.4,
      detune: 0.2,
      glide_ms: 20,
      level: 2.598,
    }),
    env: env(1, 320, 0.45, 80),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "warm-saw-bass",
    name: "Warm Saw Bass",
    role: "bass",
    family: "analog-bass",
    alternateOnly: true,
    flavour: "warm",
    params: p("mono", 36, {
      cutoff: 0.4,
      resonance: 0.2,
      env_amount: 0.45,
      env_decay: 320,
      tone: 0.35,
      drive: 0.45,
      detune: 0.35,
      glide_ms: 25,
      level: 1.955,
    }),
    env: env(2, 450, 0.75, 100),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "deep-sub",
    name: "Deep Sub",
    role: "bass",
    family: "sub-bass",
    alternateOnly: true,
    flavour: "deep",
    params: p("bass808", 33, { env_amount: 0.3, env_decay: 600, tone: 0.15, drive: 0.15, glide_ms: 50, level: 0.688 }),
    env: env(1, 100, 1, 140),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "punch-sub",
    name: "Punch Sub",
    role: "bass",
    family: "sub-bass",
    alternateOnly: true,
    flavour: "punchy",
    params: p("bass808", 36, { env_amount: 0.3, env_decay: 180, tone: 0.45, drive: 0.5, glide_ms: 30, level: 0.651 }),
    env: env(1, 100, 1, 80),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "rubber-acid",
    intense: true,
    name: "Rubber Acid",
    role: "bass",
    family: "acid-bass",
    alternateOnly: true,
    flavour: "rubbery",
    params: p("acid", 36, {
      cutoff: 0.35,
      resonance: 0.6,
      env_amount: 0.5,
      env_decay: 260,
      tone: 0,
      drive: 0.3,
      glide_ms: 45,
      level: 1.063,
    }),
    env: env(1, 350, 0.65, 70),
    mode: "gate",
    reverb: 0,
    delay: 0.1,
  },
  {
    id: "acid-pluck",
    intense: true,
    name: "Acid Pluck",
    role: "bass",
    family: "acid-bass",
    alternateOnly: true,
    flavour: "snappy",
    params: p("acid", 36, {
      cutoff: 0.2,
      resonance: 0.9,
      env_amount: 0.85,
      env_decay: 120,
      tone: 0,
      drive: 0.5,
      glide_ms: 30,
      level: 1.434,
    }),
    env: env(1, 180, 0.3, 40),
    mode: "gate",
    reverb: 0,
    delay: 0.15,
  },
  {
    id: "string-pad",
    name: "String Pad",
    role: "harmony",
    family: "pad",
    alternateOnly: true,
    flavour: "airy",
    params: p("poly", 60, {
      cutoff: 0.62,
      resonance: 0.1,
      env_amount: 0.2,
      env_decay: 1500,
      tone: 0.75,
      detune: 0.95,
      level: 0.958,
    }),
    env: env(700, 1800, 0.85, 1500),
    mode: "gate",
    reverb: 0.6,
    delay: 0.1,
  },
  {
    id: "dusk-pad",
    name: "Dusk Pad",
    role: "harmony",
    family: "pad",
    alternateOnly: true,
    flavour: "dark",
    params: p("poly", 60, {
      cutoff: 0.32,
      resonance: 0.2,
      env_amount: 0.3,
      env_decay: 1400,
      tone: 0.4,
      detune: 0.8,
      level: 1.31,
    }),
    env: env(300, 1500, 0.75, 1000),
    mode: "gate",
    reverb: 0.55,
    delay: 0.1,
  },
  {
    id: "pluck-chords",
    name: "Pluck Chords",
    role: "harmony",
    family: "stab",
    alternateOnly: true,
    flavour: "plucky",
    params: p("poly", 60, {
      cutoff: 0.45,
      resonance: 0.35,
      env_amount: 0.7,
      env_decay: 140,
      tone: 0.25,
      detune: 0.6,
      level: 2.953,
    }),
    env: env(1, 220, 0, 150),
    mode: "one_shot",
    reverb: 0.35,
    delay: 0.25,
  },
  {
    id: "soft-stab",
    name: "Soft Stab",
    role: "harmony",
    family: "stab",
    alternateOnly: true,
    flavour: "soft",
    params: p("poly", 60, {
      cutoff: 0.4,
      resonance: 0.15,
      env_amount: 0.4,
      env_decay: 300,
      tone: 0.45,
      detune: 0.75,
      level: 2.224,
    }),
    env: env(4, 450, 0, 300),
    mode: "one_shot",
    reverb: 0.4,
    delay: 0.15,
  },
  {
    id: "bell-keys",
    intense: true,
    name: "Bell Keys",
    role: "harmony",
    family: "keys",
    alternateOnly: true,
    flavour: "chiming",
    params: p("fm", 60, {
      cutoff: 0.08,
      resonance: 0.1,
      env_amount: 0.25,
      env_decay: 900,
      tone: 0.3,
      detune: 0.35,
      level: 0.537,
    }),
    env: env(1, 2000, 0.5, 450),
    mode: "gate",
    reverb: 0.35,
    delay: 0.05,
  },
  {
    id: "soft-keys",
    name: "Soft Keys",
    role: "harmony",
    family: "keys",
    alternateOnly: true,
    flavour: "velvety",
    params: p("fm", 60, {
      cutoff: 0.1,
      resonance: 0.1,
      env_amount: 0.25,
      env_decay: 1100,
      tone: 0.08,
      detune: 0.45,
      level: 0.545,
    }),
    env: env(2, 2200, 0.6, 500),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "mellow-organ",
    name: "Mellow Organ",
    role: "harmony",
    family: "organ",
    alternateOnly: true,
    flavour: "mellow",
    params: p("organ", 60, { cutoff: 0.3, resonance: 0.3, detune: 0.6, tone: 0.3, drive: 0.1, level: 0.783 }),
    env: env(5, 100, 1, 90),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "soft-lead",
    name: "Soft Lead",
    role: "lead",
    family: "ladder-lead",
    alternateOnly: true,
    flavour: "gentle",
    params: p("mono", 60, {
      cutoff: 0.4,
      resonance: 0.3,
      env_amount: 0.35,
      env_decay: 700,
      tone: 0.45,
      detune: 0.4,
      glide_ms: 120,
      level: 1.951,
    }),
    env: env(25, 600, 0.8, 300),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "saw-lead",
    intense: true,
    name: "Saw Lead",
    role: "lead",
    family: "ladder-lead",
    alternateOnly: true,
    flavour: "bright",
    params: p("mono", 60, {
      cutoff: 0.7,
      resonance: 0.35,
      env_amount: 0.4,
      env_decay: 400,
      tone: 0.7,
      detune: 0.7,
      drive: 0.2,
      glide_ms: 50,
      level: 1.168,
    }),
    env: env(3, 400, 0.75, 200),
    mode: "gate",
    reverb: 0.25,
    delay: 0.2,
  },
  {
    id: "dub-echo",
    name: "Dub Echo",
    role: "lead",
    family: "deep-lead",
    alternateOnly: true,
    flavour: "dubby",
    // A dark, filtered chord-synth stab drowned in echoes (Berlin dub techno).
    params: p("poly", 60, {
      cutoff: 0.3,
      resonance: 0.3,
      env_amount: 0.45,
      env_decay: 200,
      tone: 0.25,
      detune: 0.45,
      level: 3.266,
    }),
    env: env(1, 280, 0, 320),
    mode: "one_shot",
    reverb: 0.45,
    delay: 0.5,
  },
  {
    id: "rubber-sine",
    name: "Rubber Sine",
    role: "lead",
    family: "deep-lead",
    alternateOnly: true,
    flavour: "rubbery",
    // A round, gliding, nearly sine lead with a soft filter blip (minimal / Berlin house).
    params: p("mono", 60, {
      cutoff: 0.42,
      resonance: 0.15,
      env_amount: 0.35,
      env_decay: 380,
      tone: 0.15,
      detune: 0.1,
      glide_ms: 60,
      level: 2.18,
    }),
    env: env(4, 480, 0.7, 240),
    mode: "gate",
    reverb: 0.3,
    delay: 0.35,
  },
  {
    id: "acid-lead",
    intense: true,
    name: "Acid Lead",
    role: "lead",
    family: "acid-lead",
    alternateOnly: true,
    flavour: "wiry",
    params: p("acid", 48, {
      cutoff: 0.4,
      resonance: 0.65,
      env_amount: 0.6,
      env_decay: 320,
      tone: 0.8,
      drive: 0.45,
      glide_ms: 40,
      level: 0.649,
    }),
    env: env(1, 300, 0.6, 70),
    mode: "gate",
    reverb: 0.1,
    delay: 0.2,
  },
  {
    id: "bleep",
    intense: true,
    name: "Bleep",
    role: "lead",
    family: "acid-lead",
    alternateOnly: true,
    flavour: "bleepy",
    params: p("acid", 60, {
      cutoff: 0.5,
      resonance: 0.5,
      env_amount: 0.5,
      env_decay: 150,
      tone: 1,
      drive: 0.3,
      level: 0.899,
    }),
    env: env(1, 160, 0.3, 50),
    mode: "gate",
    reverb: 0.15,
    delay: 0.3,
  },
  // Genre families (techno, disco, garage, afro house, synthwave). Mains rotate with their kits;
  // the others are alternates of the same family.
  {
    id: "rumble-sub",
    name: "Rumble Sub",
    role: "bass",
    family: "techno-bass",
    flavour: "rumbling",
    params: p("bass808", 33, { env_amount: 0.2, env_decay: 500, tone: 0.1, drive: 0.4, level: 0.641 }),
    env: env(1, 100, 1, 80),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "rolling-bass",
    name: "Rolling Bass",
    role: "bass",
    family: "techno-bass",
    flavour: "rolling",
    params: p("mono", 36, {
      cutoff: 0.22,
      resonance: 0.45,
      env_amount: 0.55,
      env_decay: 120,
      tone: 0.15,
      drive: 0.5,
      detune: 0.1,
      level: 3.666,
    }),
    env: env(1, 160, 0, 40),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "dark-pulse",
    name: "Dark Pulse",
    role: "bass",
    family: "techno-bass",
    alternateOnly: true,
    flavour: "dark",
    params: p("mono", 36, {
      cutoff: 0.18,
      resonance: 0.6,
      env_amount: 0.4,
      env_decay: 200,
      tone: 0.5,
      drive: 0.6,
      level: 2.704,
    }),
    env: env(1, 220, 0.3, 50),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "dub-chord",
    name: "Dub Chord",
    role: "harmony",
    family: "techno-stab",
    flavour: "dubby",
    params: p("poly", 60, {
      cutoff: 0.35,
      resonance: 0.35,
      env_amount: 0.5,
      env_decay: 180,
      tone: 0.2,
      detune: 0.5,
      level: 2.951,
    }),
    env: env(1, 260, 0, 300),
    mode: "one_shot",
    reverb: 0.5,
    delay: 0.45,
  },
  {
    id: "warehouse-stab",
    intense: true,
    name: "Warehouse Stab",
    role: "harmony",
    family: "techno-stab",
    alternateOnly: true,
    flavour: "raw",
    params: p("poly", 60, {
      cutoff: 0.5,
      resonance: 0.5,
      env_amount: 0.65,
      env_decay: 110,
      drive: 0.4,
      tone: 0.3,
      detune: 0.5,
      level: 2.748,
    }),
    env: env(1, 180, 0, 120),
    mode: "one_shot",
    reverb: 0.35,
    delay: 0.3,
  },
  {
    id: "hypno-pad",
    name: "Hypno Pad",
    role: "harmony",
    family: "techno-stab",
    alternateOnly: true,
    flavour: "hypnotic",
    params: p("poly", 60, {
      cutoff: 0.28,
      resonance: 0.25,
      env_amount: 0.2,
      env_decay: 2000,
      tone: 0.5,
      detune: 0.7,
      level: 1.33,
    }),
    env: env(600, 2000, 0.8, 1500),
    mode: "gate",
    reverb: 0.55,
    delay: 0.1,
  },
  {
    id: "hoover-lead",
    intense: true,
    name: "Hoover Lead",
    role: "lead",
    family: "techno-lead",
    flavour: "hoovering",
    params: p("mono", 60, {
      cutoff: 0.55,
      resonance: 0.3,
      env_amount: 0.3,
      env_decay: 500,
      tone: 0,
      detune: 1,
      drive: 0.5,
      glide_ms: 120,
      level: 0.816,
    }),
    env: env(10, 400, 0.9, 250),
    mode: "gate",
    reverb: 0.25,
    delay: 0.2,
  },
  {
    id: "sequence-blip",
    intense: true,
    name: "Sequence Blip",
    role: "lead",
    family: "techno-lead",
    alternateOnly: true,
    flavour: "blippy",
    params: p("acid", 60, {
      cutoff: 0.35,
      resonance: 0.7,
      env_amount: 0.6,
      env_decay: 120,
      tone: 1,
      drive: 0.3,
      level: 1.201,
    }),
    env: env(1, 140, 0.2, 40),
    mode: "gate",
    reverb: 0.15,
    delay: 0.35,
  },
  {
    id: "metal-pluck",
    intense: true,
    name: "Metal Pluck",
    role: "lead",
    family: "techno-lead",
    alternateOnly: true,
    flavour: "metallic",
    params: p("fm", 72, {
      cutoff: 0.04,
      resonance: 0.15,
      env_amount: 0.2,
      env_decay: 200,
      tone: 0.33,
      detune: 0.2,
      level: 1.535,
    }),
    env: env(1, 400, 0, 200),
    mode: "one_shot",
    reverb: 0.3,
    delay: 0.3,
  },
  {
    id: "octave-bass",
    name: "Octave Bass",
    role: "bass",
    family: "disco-bass",
    flavour: "funky",
    params: p("mono", 36, {
      cutoff: 0.38,
      resonance: 0.25,
      env_amount: 0.55,
      env_decay: 180,
      tone: 0.3,
      drive: 0.35,
      detune: 0.1,
      level: 3.083,
    }),
    env: env(1, 250, 0.5, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "slap-bass",
    name: "Slap Bass",
    role: "bass",
    family: "disco-bass",
    alternateOnly: true,
    flavour: "slapped",
    params: p("fm", 36, {
      cutoff: 0.3,
      resonance: 0.2,
      env_amount: 0.6,
      env_decay: 160,
      tone: 0.17,
      drive: 0.4,
      level: 2.015,
    }),
    env: env(1, 350, 0.4, 80),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "finger-bass",
    name: "Finger Bass",
    role: "bass",
    family: "disco-bass",
    alternateOnly: true,
    flavour: "round",
    params: p("mono", 36, {
      cutoff: 0.3,
      resonance: 0.15,
      env_amount: 0.35,
      env_decay: 300,
      tone: 0.6,
      drive: 0.2,
      level: 2.39,
    }),
    env: env(3, 400, 0.6, 100),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "string-section",
    name: "String Section",
    role: "harmony",
    family: "disco-strings",
    flavour: "soaring",
    params: p("poly", 60, {
      cutoff: 0.6,
      resonance: 0.1,
      env_amount: 0.15,
      env_decay: 1500,
      tone: 0.7,
      detune: 1,
      level: 0.999,
    }),
    env: env(250, 1500, 0.85, 700),
    mode: "gate",
    reverb: 0.45,
    delay: 0.05,
  },
  {
    id: "brass-section",
    name: "Brass Section",
    role: "harmony",
    family: "disco-strings",
    alternateOnly: true,
    flavour: "brassy",
    params: p("poly", 60, {
      cutoff: 0.45,
      resonance: 0.2,
      env_amount: 0.6,
      env_decay: 250,
      tone: 0.1,
      detune: 0.6,
      level: 1.37,
    }),
    env: env(25, 300, 0.75, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "synth-strings",
    name: "Synth Strings",
    role: "harmony",
    family: "disco-strings",
    alternateOnly: true,
    flavour: "silky",
    params: p("poly", 60, {
      cutoff: 0.5,
      resonance: 0.1,
      env_amount: 0.1,
      env_decay: 1500,
      tone: 0.5,
      detune: 0.9,
      level: 1.118,
    }),
    env: env(400, 1500, 0.85, 900),
    mode: "gate",
    reverb: 0.5,
    delay: 0,
  },
  {
    id: "disco-epiano",
    name: "Disco Keys",
    role: "harmony",
    family: "disco-keys",
    flavour: "glossy",
    params: p("fm", 60, {
      cutoff: 0.12,
      resonance: 0.15,
      env_amount: 0.4,
      env_decay: 800,
      tone: 0.17,
      detune: 0.5,
      level: 0.864,
    }),
    env: env(1, 1600, 0.3, 400),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "clav-funk",
    name: "Funk Clav",
    role: "harmony",
    family: "disco-keys",
    alternateOnly: true,
    flavour: "clucky",
    params: p("poly", 60, {
      cutoff: 0.7,
      resonance: 0.4,
      env_amount: 0.5,
      env_decay: 90,
      tone: 0.9,
      detune: 0.2,
      level: 4.535,
    }),
    env: env(1, 150, 0.1, 60),
    mode: "gate",
    reverb: 0.15,
    delay: 0,
  },
  {
    id: "funk-organ",
    name: "Funk Organ",
    role: "harmony",
    family: "disco-keys",
    alternateOnly: true,
    flavour: "greasy",
    params: p("organ", 60, { cutoff: 0.15, resonance: 0.4, tone: 0.33, detune: 0.4, drive: 0.25, level: 0.608 }),
    env: env(3, 100, 1, 70),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "disco-lead",
    name: "Disco Lead",
    role: "lead",
    family: "disco-lead",
    flavour: "shiny",
    params: p("mono", 60, {
      cutoff: 0.5,
      resonance: 0.25,
      env_amount: 0.35,
      env_decay: 400,
      tone: 0.2,
      detune: 0.6,
      glide_ms: 40,
      level: 1.869,
    }),
    env: env(5, 400, 0.8, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "sparkle-bell",
    intense: true,
    name: "Sparkle Bell",
    role: "lead",
    family: "disco-lead",
    alternateOnly: true,
    flavour: "sparkly",
    params: p("fm", 72, { cutoff: 0.03, env_amount: 0.22, env_decay: 700, tone: 0.17, detune: 0.3, level: 1.174 }),
    env: env(1, 900, 0, 500),
    mode: "one_shot",
    reverb: 0.4,
    delay: 0.3,
  },
  {
    id: "funk-whistle",
    intense: true,
    name: "Funk Organ",
    role: "lead",
    family: "disco-lead",
    alternateOnly: true,
    flavour: "whistling",
    params: p("organ", 72, { cutoff: 0.15, resonance: 0.15, tone: 0.45, detune: 0.2, level: 0.752 }),
    env: env(15, 100, 1, 120),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "organ-bass",
    name: "Organ Bass",
    role: "bass",
    family: "garage-bass",
    flavour: "punchy",
    params: p("organ", 36, { cutoff: 0.1, resonance: 0.6, tone: 0, detune: 0.1, drive: 0.5, level: 1 }),
    env: env(2, 100, 1, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "wobble-sub",
    intense: true,
    name: "Wobble Sub",
    role: "bass",
    family: "garage-bass",
    alternateOnly: true,
    flavour: "wobbly",
    params: p("mono", 36, {
      cutoff: 0.25,
      resonance: 0.5,
      env_amount: 0.6,
      env_decay: 300,
      tone: 0.8,
      drive: 0.5,
      glide_ms: 60,
      level: 1.537,
    }),
    env: env(1, 300, 0.6, 80),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "two-step-sub",
    name: "Two-Step Sub",
    role: "bass",
    family: "garage-bass",
    flavour: "bouncy",
    params: p("bass808", 36, { env_amount: 0.22, env_decay: 260, tone: 0.3, drive: 0.3, glide_ms: 70, level: 0.693 }),
    env: env(1, 100, 1, 90),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "garage-chord",
    name: "Garage Organ",
    role: "harmony",
    family: "garage-chords",
    flavour: "stabby",
    params: p("organ", 60, { cutoff: 0.05, resonance: 0.7, tone: 0.33, drive: 0.3, detune: 0.3, level: 1.207 }),
    env: env(1, 250, 0, 120),
    mode: "one_shot",
    reverb: 0.3,
    delay: 0.2,
  },
  {
    id: "skippy-stab",
    name: "Skippy Stab",
    role: "harmony",
    family: "garage-chords",
    alternateOnly: true,
    flavour: "skippy",
    params: p("poly", 60, {
      cutoff: 0.55,
      resonance: 0.3,
      env_amount: 0.6,
      env_decay: 120,
      tone: 0.3,
      detune: 0.5,
      level: 3.411,
    }),
    env: env(1, 160, 0, 100),
    mode: "one_shot",
    reverb: 0.3,
    delay: 0.3,
  },
  {
    id: "velvet-pad",
    name: "Velvet Pad",
    role: "harmony",
    family: "garage-chords",
    alternateOnly: true,
    flavour: "velvety",
    params: p("poly", 60, {
      cutoff: 0.35,
      resonance: 0.15,
      env_amount: 0.2,
      env_decay: 1500,
      tone: 0.5,
      detune: 0.8,
      level: 1.297,
    }),
    env: env(300, 1500, 0.8, 900),
    mode: "gate",
    reverb: 0.5,
    delay: 0,
  },
  {
    id: "vocal-pluck",
    intense: true,
    name: "Vox Pluck",
    role: "lead",
    family: "garage-lead",
    flavour: "vocal",
    params: p("fm", 72, {
      cutoff: 0.04,
      resonance: 0.05,
      env_amount: 0.2,
      env_decay: 250,
      tone: 0.33,
      detune: 0.3,
      level: 1.417,
    }),
    env: env(1, 350, 0.2, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.3,
  },
  {
    id: "garage-bell",
    intense: true,
    name: "Garage Bell",
    role: "lead",
    family: "garage-lead",
    alternateOnly: true,
    flavour: "chiming",
    params: p("fm", 72, { cutoff: 0.06, env_amount: 0.35, env_decay: 500, tone: 0.17, level: 1.476 }),
    env: env(1, 800, 0, 400),
    mode: "one_shot",
    reverb: 0.35,
    delay: 0.25,
  },
  {
    id: "smooth-lead",
    name: "Smooth Lead",
    role: "lead",
    family: "garage-lead",
    alternateOnly: true,
    flavour: "smooth",
    params: p("mono", 60, {
      cutoff: 0.6,
      resonance: 0.2,
      env_amount: 0.2,
      env_decay: 400,
      tone: 0.1,
      detune: 0.2,
      glide_ms: 90,
      level: 1.369,
    }),
    env: env(10, 500, 0.8, 250),
    mode: "gate",
    reverb: 0.3,
    delay: 0.2,
  },
  {
    id: "log-drum",
    name: "Log Drum",
    role: "bass",
    family: "afro-bass",
    flavour: "knocking",
    params: p("bass808", 45, { env_amount: 0.9, env_decay: 140, tone: 0.6, drive: 0.6, level: 0.616 }),
    env: env(1, 100, 1, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "afro-sub",
    name: "Afro Sub",
    role: "bass",
    family: "afro-bass",
    alternateOnly: true,
    flavour: "deep",
    params: p("bass808", 33, { env_amount: 0.3, env_decay: 450, tone: 0.2, drive: 0.25, level: 0.673 }),
    env: env(1, 100, 1, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "warm-pluck-bass",
    name: "Warm Pluck Bass",
    role: "bass",
    family: "afro-bass",
    alternateOnly: true,
    flavour: "warm",
    params: p("mono", 36, {
      cutoff: 0.28,
      resonance: 0.2,
      env_amount: 0.5,
      env_decay: 160,
      tone: 0.4,
      drive: 0.3,
      level: 3.768,
    }),
    env: env(1, 250, 0.35, 70),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "flute-lead",
    name: "Flute",
    role: "lead",
    family: "afro-lead",
    flavour: "breathy",
    // The physical flute (breath, chiff, register-dependent overtones), low and breathy for
    // depth — it was a two-operator FM tone, which sounded like a toy keyboard up high.
    params: p("flute", 72, {
      cutoff: 0.5,
      tone: 0.5,
      drive: 0.42,
      env_amount: 0.4,
      env_decay: 260,
      glide_ms: 0,
      level: 1,
    }),
    env: env(35, 400, 0.9, 200),
    mode: "gate",
    reverb: 0.42,
    delay: 0.15,
  },
  {
    id: "bright-pluck",
    intense: true,
    name: "Bright Pluck",
    role: "lead",
    family: "afro-lead",
    alternateOnly: true,
    flavour: "bright",
    params: p("poly", 72, { cutoff: 0.6, resonance: 0.3, env_amount: 0.6, env_decay: 150, tone: 0.4, level: 2.874 }),
    env: env(1, 250, 0, 180),
    mode: "one_shot",
    reverb: 0.3,
    delay: 0.3,
  },
  {
    id: "afro-bell",
    intense: true,
    name: "Afro Bell",
    role: "lead",
    family: "afro-lead",
    alternateOnly: true,
    flavour: "ringing",
    params: p("fm", 72, { cutoff: 0.05, env_amount: 0.2, env_decay: 400, tone: 0.17, level: 1.676 }),
    env: env(1, 600, 0, 300),
    mode: "one_shot",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "arp-bass",
    name: "Arp Bass",
    role: "bass",
    family: "synthwave-bass",
    flavour: "driving",
    params: p("mono", 36, {
      cutoff: 0.3,
      resonance: 0.3,
      env_amount: 0.5,
      env_decay: 140,
      tone: 0,
      detune: 0.3,
      drive: 0.3,
      level: 2.542,
    }),
    env: env(1, 180, 0.3, 50),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "analog-sub-saw",
    name: "Analog Saw Bass",
    role: "bass",
    family: "synthwave-bass",
    alternateOnly: true,
    flavour: "fat",
    params: p("mono", 36, {
      cutoff: 0.45,
      resonance: 0.15,
      env_amount: 0.3,
      env_decay: 300,
      tone: 0.1,
      detune: 0.6,
      drive: 0.25,
      glide_ms: 30,
      level: 1.63,
    }),
    env: env(2, 400, 0.8, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "outrun-pulse",
    name: "Outrun Pulse",
    role: "bass",
    family: "synthwave-bass",
    alternateOnly: true,
    flavour: "pulsing",
    params: p("mono", 36, {
      cutoff: 0.35,
      resonance: 0.35,
      env_amount: 0.45,
      env_decay: 200,
      tone: 0.9,
      drive: 0.3,
      level: 1.788,
    }),
    env: env(1, 250, 0.5, 70),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "polysynth-pad",
    name: "Polysynth Pad",
    role: "harmony",
    family: "synthwave-pad",
    flavour: "nostalgic",
    // Smooth: a wide, barely swept pulse (no nasal narrowing), no filter peak, a gentle attack
    // bloom and a soft chorus.
    params: p("poly", 60, {
      cutoff: 0.43,
      resonance: 0.05,
      env_amount: 0.1,
      env_decay: 1200,
      tone: 0.15,
      detune: 0.5,
      level: 1.208,
    }),
    env: env(300, 1200, 0.85, 1200),
    mode: "gate",
    reverb: 0.6,
    delay: 0.15,
  },
  {
    id: "brass-poly",
    name: "Brass Poly",
    role: "harmony",
    family: "synthwave-pad",
    alternateOnly: true,
    flavour: "heroic",
    params: p("poly", 60, {
      cutoff: 0.4,
      resonance: 0.2,
      env_amount: 0.65,
      env_decay: 300,
      tone: 0.1,
      detune: 0.7,
      level: 1.392,
    }),
    env: env(20, 350, 0.75, 300),
    mode: "gate",
    reverb: 0.4,
    delay: 0,
  },
  {
    id: "glass-keys",
    intense: true,
    name: "Glass Keys",
    role: "harmony",
    family: "synthwave-pad",
    alternateOnly: true,
    flavour: "glassy",
    // A softer glass: lower FM brightness and attack (2.5–8 kHz energy 1.3 % → 0.12 %).
    params: p("fm", 60, {
      cutoff: 0.12,
      resonance: 0.1,
      env_amount: 0.22,
      env_decay: 900,
      tone: 0.33,
      detune: 0.5,
      level: 0.642,
    }),
    env: env(1, 1500, 0.35, 500),
    mode: "gate",
    reverb: 0.45,
    delay: 0.25,
  },
  {
    id: "saw-solo",
    name: "Saw Solo",
    role: "lead",
    family: "synthwave-lead",
    flavour: "soaring",
    // Smooth, not harsh: a gentler filter (no peak), less detune and drive (2.5–8 kHz energy
    // 4.2 % → 0.1 %).
    params: p("mono", 60, {
      cutoff: 0.52,
      resonance: 0.12,
      env_amount: 0.25,
      env_decay: 500,
      tone: 0,
      detune: 0.45,
      drive: 0.12,
      glide_ms: 70,
      level: 1.223,
    }),
    env: env(5, 500, 0.85, 300),
    mode: "gate",
    reverb: 0.35,
    delay: 0.35,
  },
  {
    id: "square-lead",
    name: "Square Lead",
    role: "lead",
    family: "synthwave-lead",
    alternateOnly: true,
    flavour: "hollow",
    params: p("mono", 60, {
      cutoff: 0.5,
      resonance: 0.2,
      env_amount: 0.3,
      env_decay: 400,
      tone: 1,
      detune: 0.4,
      glide_ms: 50,
      level: 0.796,
    }),
    env: env(5, 400, 0.8, 250),
    mode: "gate",
    reverb: 0.3,
    delay: 0.3,
  },
  {
    id: "sync-sweep",
    intense: true,
    name: "Sync Sweep",
    role: "lead",
    family: "synthwave-lead",
    alternateOnly: true,
    flavour: "sweeping",
    params: p("acid", 60, {
      cutoff: 0.45,
      resonance: 0.55,
      env_amount: 0.5,
      env_decay: 400,
      tone: 0.5,
      drive: 0.3,
      level: 1.175,
    }),
    env: env(3, 450, 0.7, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.3,
  },

  // Classic instruments (orchestral, acoustic, guitars): acoustic-style models (engine
  // rt/acoustic.rs). The planner gives every day at least one of them.
  {
    id: "grand-piano",
    name: "Grand Piano",
    role: "classic",
    family: "piano",
    classic: true,
    flavour: "rich",
    params: p("piano", 60, { cutoff: 0.6, tone: 0.55, env_decay: 3500, detune: 0.35, level: 2.842 }),
    env: env(1, 9000, 1, 380),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  {
    id: "upright-piano",
    name: "Upright Piano",
    role: "classic",
    family: "piano",
    classic: true,
    alternateOnly: true,
    flavour: "homely",
    params: p("piano", 60, { cutoff: 0.5, tone: 0.45, env_decay: 2600, detune: 0.6, level: 3.421 }),
    env: env(1, 9000, 1, 300),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "felt-piano",
    name: "Felt Piano",
    role: "classic",
    family: "piano",
    classic: true,
    alternateOnly: true,
    flavour: "soft",
    params: p("piano", 60, { cutoff: 0.15, tone: 0.2, env_decay: 3000, detune: 0.4, level: 3.144 }),
    env: env(1, 9000, 1, 450),
    mode: "gate",
    reverb: 0.4,
    delay: 0.1,
  },
  {
    id: "violin-section",
    name: "Violin Section",
    role: "classic",
    family: "orchestral-strings",
    classic: true,
    flavour: "lush",
    params: p("bowed", 60, {
      cutoff: 0.62,
      tone: 0.55,
      detune: 0.6,
      env_amount: 0.5,
      env_decay: 350,
      drive: 0.15,
      level: 1.085,
    }),
    env: env(180, 400, 0.9, 500),
    mode: "gate",
    reverb: 0.45,
    delay: 0,
  },
  {
    id: "chamber-strings",
    name: "Chamber Strings",
    role: "classic",
    family: "orchestral-strings",
    classic: true,
    alternateOnly: true,
    flavour: "intimate",
    params: p("bowed", 60, {
      cutoff: 0.55,
      tone: 0.7,
      detune: 0.35,
      env_amount: 0.6,
      env_decay: 250,
      drive: 0.25,
      level: 1.233,
    }),
    env: env(120, 400, 0.85, 400),
    mode: "gate",
    reverb: 0.35,
    delay: 0,
  },
  {
    id: "pizzicato-strings",
    name: "Pizzicato Strings",
    role: "classic",
    family: "orchestral-strings",
    classic: true,
    alternateOnly: true,
    flavour: "plucky",
    params: p("pluck", 60, {
      cutoff: 0.22,
      resonance: 0.4,
      tone: 0.1,
      env_decay: 450,
      detune: 0.8,
      drive: 0,
      level: 2.781,
    }),
    env: env(1, 600, 0, 200),
    mode: "one_shot",
    reverb: 0.4,
    delay: 0,
  },
  {
    id: "classical-guitar",
    name: "Classical Guitar",
    role: "classic",
    family: "acoustic-guitar",
    classic: true,
    flavour: "warm",
    params: p("pluck", 60, {
      cutoff: 0.5,
      resonance: 0.35,
      tone: 0.35,
      env_decay: 2200,
      detune: 0.7,
      drive: 0,
      level: 1.203,
    }),
    env: env(1, 6000, 1, 250),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "steel-guitar",
    name: "Steel Guitar",
    role: "classic",
    family: "acoustic-guitar",
    classic: true,
    alternateOnly: true,
    flavour: "bright",
    params: p("pluck", 60, {
      cutoff: 0.72,
      resonance: 0.2,
      tone: 0.7,
      env_decay: 2800,
      detune: 0.55,
      drive: 0,
      level: 1.012,
    }),
    env: env(1, 6000, 1, 250),
    mode: "gate",
    reverb: 0.25,
    delay: 0,
  },
  {
    id: "concert-harp",
    name: "Concert Harp",
    role: "classic",
    family: "acoustic-guitar",
    classic: true,
    alternateOnly: true,
    flavour: "glowing",
    params: p("pluck", 60, {
      cutoff: 0.42,
      resonance: 0.5,
      tone: 0.55,
      env_decay: 3500,
      detune: 0.3,
      drive: 0,
      level: 1.047,
    }),
    env: env(1, 6000, 1, 600),
    mode: "gate",
    reverb: 0.5,
    delay: 0.1,
  },
  {
    id: "solo-violin",
    name: "Solo Violin",
    role: "classic",
    family: "solo-strings",
    classic: true,
    flavour: "singing",
    params: p("bowed", 72, {
      cutoff: 0.68,
      tone: 0.65,
      detune: 0,
      env_amount: 0.6,
      env_decay: 220,
      drive: 0.2,
      level: 1.077,
    }),
    env: env(70, 300, 0.9, 260),
    mode: "gate",
    reverb: 0.35,
    delay: 0.1,
  },
  {
    id: "viola",
    name: "Viola",
    role: "classic",
    family: "solo-strings",
    classic: true,
    alternateOnly: true,
    flavour: "dusky",
    params: p("bowed", 72, {
      cutoff: 0.55,
      tone: 0.75,
      detune: 0,
      env_amount: 0.5,
      env_decay: 260,
      drive: 0.25,
      level: 1.235,
    }),
    env: env(80, 300, 0.9, 280),
    mode: "gate",
    reverb: 0.35,
    delay: 0.1,
  },
  {
    id: "violin-espressivo",
    name: "Violin Espressivo",
    role: "classic",
    family: "solo-strings",
    classic: true,
    alternateOnly: true,
    flavour: "expressive",
    params: p("bowed", 72, {
      cutoff: 0.7,
      tone: 0.6,
      detune: 0.08,
      env_amount: 0.9,
      env_decay: 120,
      drive: 0.15,
      level: 1.202,
    }),
    env: env(50, 300, 0.95, 300),
    mode: "gate",
    reverb: 0.4,
    delay: 0.15,
  },
  {
    id: "trumpet",
    name: "Trumpet",
    role: "classic",
    family: "classic-brass",
    classic: true,
    flavour: "bold",
    params: p("brass", 72, {
      cutoff: 0.42,
      resonance: 0.25,
      env_amount: 0.6,
      env_decay: 350,
      tone: 0,
      detune: 0,
      drive: 0.2,
      level: 1.557,
    }),
    env: env(8, 300, 0.85, 120),
    mode: "gate",
    reverb: 0.3,
    delay: 0.1,
  },
  {
    id: "french-horn",
    name: "French Horn",
    role: "classic",
    family: "classic-brass",
    classic: true,
    alternateOnly: true,
    flavour: "noble",
    params: p("brass", 72, {
      cutoff: 0.3,
      resonance: 0.15,
      env_amount: 0.4,
      env_decay: 500,
      tone: 0,
      detune: 0.3,
      drive: 0.05,
      level: 1.743,
    }),
    env: env(30, 300, 0.9, 220),
    mode: "gate",
    reverb: 0.45,
    delay: 0.05,
  },
  {
    id: "muted-trumpet",
    name: "Muted Trumpet",
    role: "classic",
    family: "classic-brass",
    classic: true,
    alternateOnly: true,
    flavour: "smoky",
    params: p("brass", 72, {
      cutoff: 0.5,
      resonance: 0.3,
      env_amount: 0.45,
      env_decay: 300,
      tone: 0.85,
      detune: 0,
      drive: 0.1,
      level: 1.422,
    }),
    env: env(8, 300, 0.85, 140),
    mode: "gate",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "concert-flute",
    name: "Concert Flute",
    role: "classic",
    family: "classic-flute",
    classic: true,
    flavour: "airy",
    params: p("flute", 72, { cutoff: 0.7, tone: 0.55, drive: 0.35, env_amount: 0.5, env_decay: 280, level: 0.857 }),
    env: env(35, 300, 0.9, 180),
    mode: "gate",
    reverb: 0.4,
    delay: 0.15,
  },
  {
    id: "alto-flute",
    name: "Alto Flute",
    role: "classic",
    family: "classic-flute",
    classic: true,
    alternateOnly: true,
    flavour: "breathy",
    params: p("flute", 72, { cutoff: 0.55, tone: 0.3, drive: 0.5, env_amount: 0.45, env_decay: 320, level: 0.907 }),
    env: env(45, 300, 0.9, 220),
    mode: "gate",
    reverb: 0.45,
    delay: 0.15,
  },
  {
    id: "wooden-flute",
    name: "Wooden Flute",
    role: "classic",
    family: "classic-flute",
    classic: true,
    alternateOnly: true,
    flavour: "earthy",
    params: p("flute", 72, { cutoff: 0.5, tone: 0.45, drive: 0.35, env_amount: 0.35, env_decay: 200, level: 0.965 }),
    env: env(30, 300, 0.9, 160),
    mode: "gate",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "e-guitar",
    name: "E-Guitar Clean",
    role: "classic",
    family: "electric-guitar",
    classic: true,
    flavour: "glassy",
    // A single-coil solid body on its neck + middle pickups into a clean amp: smooth and glassy.
    // (Pluck electric: detune = pickup selector, drive ≤ 0.3 = clean amp, env_amount = amp brightness.)
    params: p("pluck", 60, {
      cutoff: 0.55,
      resonance: 0.2,
      tone: 0.9,
      env_amount: 0.5,
      env_decay: 7000,
      detune: 0.25,
      drive: 0.15,
      level: 1.464,
    }),
    env: env(1, 6000, 1, 200),
    mode: "gate",
    reverb: 0.25,
    delay: 0.15,
  },
  {
    id: "e-guitar-crunch",
    name: "E-Guitar Drive",
    role: "classic",
    family: "electric-guitar",
    classic: true,
    alternateOnly: true,
    flavour: "gritty",
    // The bridge pickup through an overdrive into a cranked amp and a closed 4×12.
    params: p("pluck", 60, {
      cutoff: 0.62,
      resonance: 0.15,
      tone: 0.88,
      env_amount: 0.45,
      env_decay: 6000,
      detune: 1,
      drive: 0.6,
      // Finger vibrato (cents) on held notes.
      glide_ms: 12,
      level: 0.639,
    }),
    env: env(1, 6000, 1, 200),
    mode: "gate",
    reverb: 0.25,
    delay: 0.15,
  },
  {
    id: "e-guitar-lead",
    name: "E-Guitar Lead",
    role: "classic",
    family: "electric-guitar",
    classic: true,
    alternateOnly: true,
    flavour: "soaring",
    // The neck pickup with high gain: a singing, sustaining lead tone.
    params: p("pluck", 60, {
      cutoff: 0.58,
      resonance: 0.2,
      tone: 0.92,
      env_amount: 0.4,
      env_decay: 7000,
      detune: 0,
      drive: 0.85,
      // A singing finger vibrato (cents) on held notes.
      glide_ms: 25,
      level: 0.6,
    }),
    env: env(1, 6000, 1, 300),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
];

/** Every preset: the core list and the newer families (supersaws, vocal leads, Reese basses,
 *  mallets, reeds: presetsMore.ts). */
export const SYNTH_PRESETS: readonly SynthPreset[] = [...CORE_PRESETS, ...MORE_PRESETS];

export const PRESETS_BY_ID: ReadonlyMap<string, SynthPreset> = new Map(SYNTH_PRESETS.map((x) => [x.id, x]));

/** Presets that can be a day's main instrument for `role`. */
export function presetsForRole(role: InstrumentRole): SynthPreset[] {
  return SYNTH_PRESETS.filter((x) => x.role === role && !x.alternateOnly);
}

/** Every preset of a family (main candidates first, then alternates, in list order). */
export function familyOf(id: string): SynthPreset[] {
  const main = getPreset(id);
  return SYNTH_PRESETS.filter((x) => x.family === main.family && x.role === main.role);
}

/**
 * The two alternates a day offers next to its main preset `id`: other members of its family,
 * picked by `seed` (deterministic). Alternate-only presets go first, so the main presets keep
 * their own days.
 */
export function pickAlternates(id: string, seed: number): [string, string] {
  const mainId = getPreset(id).id; // retired ids resolve to their replacement
  const others = familyOf(mainId).filter((x) => x.id !== mainId);
  const ordered = [...others.filter((x) => x.alternateOnly), ...others.filter((x) => !x.alternateOnly)];
  if (ordered.length < 2) throw new Error(`preset family of "${id}" needs at least 3 members`);
  // Two of the candidates, chosen by the seed (keeps list order, so the result is stable).
  const n = ordered.length;
  const skip = n > 2 ? (seed >>> 0) % n : -1;
  const pick = ordered.filter((_, i) => i !== skip).slice(0, 2);
  return [pick[0].id, pick[1].id];
}

/** Retired presets and what replaced them (days planned earlier keep working). */
export const PRESET_ALIASES: Readonly<Record<string, string>> = {
  "glass-bell": "deep-organ",
  "music-box": "dub-echo",
  mallet: "rubber-sine",
  marimba: "highlife-keys",
  kalimba: "warm-vibes",
  "steel-drum": "highlife-organ",
  mbira: "vibraphone",
  "steel-pan": "vibraphone",
  "marimba-wood": "vibraphone",
};

export function getPreset(id: string): SynthPreset {
  const preset = PRESETS_BY_ID.get(PRESET_ALIASES[id] ?? id);
  if (!preset) throw new Error(`unknown synth preset "${id}"`);
  return preset;
}

/** Playable MIDI note range per role (the piano roll shows the in-key notes of this range). */
export const ROLE_RANGE: Record<InstrumentRole, { lo: number; hi: number }> = {
  bass: { lo: 28, hi: 55 },
  harmony: { lo: 48, hi: 79 },
  lead: { lo: 55, hi: 91 },
  // Classics play melodies and chords across the middle: piano, strings, guitars, brass, flutes.
  classic: { lo: 48, hi: 88 },
};
